import React, {
  useEffect,
  useState
} from "react";

import {
  collection,
  setDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";
import { reserveItemId } from "../../services/itemIdService";

export default function ItemMaster() {

  const [items, setItems] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [migrating, setMigrating] =
    useState(false);

  // ✅ Vendor state
  const [vendors, setVendors] = useState([]);

  // ✅ Edit state
  const [editingId,   setEditingId]   = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [editSaving,  setEditSaving]  = useState(false);

  const [form, setForm] =
    useState({
      item_name:              "",
      specification:          "",
      item_code:              "",
      unit:                   "nos",
      category:               "MATERIAL",
      minimum_stock:          0,
      preferred_vendor_id:    "",
      preferred_vendor_name:  "",
    });

  // ── SEARCH (Item Master list) ──────────────────────────────────────────
  // Client-side filter — Item Master already loads all active items in one
  // query (fetchItems() above), so a second query per keystroke would be
  // an N+1 anti-pattern. Filters across item_name + specification +
  // item_code so a technician can find an item whether they remember the
  // short name ("copper pipe") or a spec detail ("22.2mm").
  const [searchTerm, setSearchTerm] = useState("");

  const user      = JSON.parse(localStorage.getItem("user") || "{}");
const companyId = user?.company_id || "";

  // =================================================
  // NORMALIZE
  // =================================================

  const normalize =
    (val = "") => {

      return String(val)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
    };

  // =================================================
  // CATEGORY PREFIX
  // =================================================

  const categoryPrefixMap = {

    MATERIAL: "MT",

    TOOLS: "TL",

    CONSUMABLE: "CS",

    ASSET: "AS",
  };
  // =================================================
  // GENERATE ITEM ID
  // =================================================
// eslint-disable-next-line no-unused-vars
  const generateItemId =
    async () => {

      const snap =
  await getDocs(
    query(
      collection(db, "items"),
      where("company_id", "==", companyId)
    )
  );

      const ids = [];

      snap.forEach(
        (docSnap) => {

          const data =
            docSnap.data();

          const itemId =
            String(
              data.item_id || ""
            ).trim();

          if (
            /^ITEM-\d+$/.test(
              itemId
            )
          ) {

            const num =
              Number(

                itemId.replace(
                  "ITEM-",
                  ""
                )
              );

            ids.push(num);
          }
        }
      );

      const nextId =

        ids.length > 0

          ? Math.max(...ids) + 1

          : 1;

      return `ITEM-${String(
        nextId
      ).padStart(5, "0")}`;
    };

  // =================================================
  // FETCH ITEMS
  // =================================================

  const fetchItems =
    async () => {

      setLoading(true);

      try {

        const q = query(
  collection(db, "items"),
  where("company_id", "==", companyId),
  where("is_active",  "==", true)
);

        const snap =
          await getDocs(q);

        const list =
          snap.docs.map(
            (docSnap) => ({

              id:
                docSnap.id,

              ...docSnap.data(),
            })
          );

        list.sort(
          (a, b) =>

            (
              a.item_name || ""
            ).localeCompare(

              b.item_name || ""
            )
        );

        setItems(list);

      } catch (err) {

        console.error(
          "Fetch Items Error:",
          err
        );
      }

      setLoading(false);
    };

  // =================================================
  // FETCH VENDORS
  // =================================================
  const fetchVendors = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "vendors"),
        where("company_id", "==", companyId)
      ));
      setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Fetch Vendors Error:", err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { fetchItems(); fetchVendors(); }, []);

  // =================================================
  // HANDLE CHANGE
  // =================================================

  const handleChange =
    (e) => {

      const {
        name,
        value
      } = e.target;

      let updatedForm = {

        ...form,

        [name]: value,
      };

      if (

        name ===
          "item_name" ||

        name ===
          "category"

      ) {

        const prefix =

          categoryPrefixMap[

            name === "category"

              ? value

              : updatedForm.category

          ] || "IT";

        const shortCode =

          (
            name ===
              "item_name"

              ? value

              : updatedForm.item_name
          )

            .toUpperCase()

            .trim()

            .replace(
              /[^A-Z0-9 ]/g,
              ""
            )

            .split(" ")

            .filter(Boolean)

            .slice(0, 3)

            .map(
              (word) =>
                word.substring(
                  0,
                  4
                )
            )

            .join("-");

        updatedForm.item_code =
          shortCode

            ? `${prefix}-${shortCode}`

            : "";
      }

      setForm(updatedForm);
    };

  // =================================================
  // RESET FORM
  // =================================================

  const resetForm =
    () => {
      setForm({
        item_name:             "",
        specification:         "",
        item_code:             "",
        unit:                  "",
        category:              "MATERIAL",
        minimum_stock:         0,
        preferred_vendor_id:   "",
        preferred_vendor_name: "",
      });
    };

// =================================================
// ADD ITEM
// =================================================

const handleSubmit =
  async () => {

    if (!form.item_name?.trim()) {
      alert("Item Name is required."); return;
    }
    if (!form.item_code?.trim()) {
      alert("Item Code is required."); return;
    }
    if (!form.unit) {
      alert("Please select a Unit (meter / nos / kg etc)."); return;
    }
    if (!form.category) {
      alert("Please select a Category (Material or Consumable)."); return;
    }

    const normalizedName =
      normalize(
        form.item_name
      );

    const normalizedCode =
      normalize(
        form.item_code
      );

    const duplicate =
      items.find(
        (x) =>

          normalize(
            x.item_name
          ) ===
            normalizedName ||

          normalize(
            x.item_code
          ) ===
            normalizedCode
      );

    if (duplicate) {

      alert(
        "Duplicate Item Name or Code Found"
      );

      return;
    }

    setLoading(true);

    try {

      // =========================================
      // CREATE FIRESTORE DOC FIRST
      // =========================================

      const newItemRef =
  doc(
    collection(
      db,
      "items"
    )
  );

// =========================================
// GENERATE ITEM-XXXX item_id
// =========================================

// D-300: platform-wide id (old generateItemId() was per-company — unused now).
const itemId = await reserveItemId();

      // =========================================
      // SAVE ITEM MASTER
      // =========================================

      await setDoc(
        newItemRef,
        {
          item_id:               itemId,
          ...form,
          preferred_vendor_id:   form.preferred_vendor_id   || "",
          preferred_vendor_name: form.preferred_vendor_name || "",
          company_id:            companyId,
          is_active:             true,
          created_at:            serverTimestamp(),
          updated_at:            serverTimestamp(),
        }
      );

      // =========================================
      // CREATE INVENTORY SUMMARY
      // =========================================

      await setDoc(

        doc(
          db,
          "inventory_summary",
          itemId
        ),

        {

          company_id:
            companyId,

          item_id:
            itemId,

          item_master_doc_id:
            itemId,

          item_name:
            form.item_name,

          specification:
            form.specification || "",

          item_code:
            form.item_code,

          category:
            form.category,

          unit:
            form.unit,

           qty:           0,
          defective_stock: 0,
          scrap_stock:   0,
          minimum_stock: Number(form.minimum_stock || 0),
          created_at:    serverTimestamp(),
          updated_at:    serverTimestamp(),
        }
      );

      alert(
        "Item Added Successfully"
      );

      resetForm();

      fetchItems();

    } catch (err) {

      console.error(
        "Save Item Error:",
        err
      );

      alert(
        "Failed to save item"
      );
    }

    setLoading(false);
  };

  // =================================================
  // MIGRATE EXISTING ITEMS
  // =================================================

  const migrateExistingItems =
    async () => {

      if (

        !window.confirm(

          "Run Item Master Migration?"
        )
      ) {

        return;
      }

      try {

        setMigrating(true);

        const snap = await getDocs(
  query(collection(db, "items"), where("company_id", "==", companyId))
);

        let counter = 1;

        for (const docSnap of snap.docs) {

          const data =
            docSnap.data();

          // ALREADY MIGRATED

          if (
            data.item_id
          ) {

            continue;
          }

          const itemId =
            `ITEM-${String(
              counter
            ).padStart(5, "0")}`;

          counter++;

          // =====================================
          // UPDATE ITEM MASTER
          // =====================================

          await updateDoc(

            doc(
              db,
              "items",
              docSnap.id
            ),

            {

              item_id:
                itemId,

              updated_at:
                serverTimestamp(),
            }
          );

          // =====================================
          // CREATE / UPDATE INVENTORY SUMMARY
          // =====================================

          await setDoc(

            doc(
              db,
              "inventory_summary",
              itemId
            ),

            {

              company_id:

                data.company_id ||

                companyId,

              item_id:
                itemId,

              firebase_item_doc_id:
                docSnap.id,

              item_name:
                data.item_name || "",

              item_code:
                data.item_code || "",

              category:
                data.category || "",

              unit:
                data.unit || "nos",

              qty: 0,

              defective_stock: 0,

              scrap_stock: 0,

              updated_at:
                serverTimestamp(),
            },

            {
              merge: true
            }
          );

          console.log(
            "ITEM MIGRATED:",
            docSnap.id,
            "→",
            itemId
          );
        }

        alert(
          "Item Master Migration Complete"
        );

        fetchItems();

      } catch (error) {

        console.error(
          "Migration Error:",
          error
        );

        alert(
          "Migration Failed"
        );

      } finally {

        setMigrating(false);
      }
    };

    // =================================================
  // SYNC INVENTORY SUMMARY CATEGORIES FROM ITEMS MASTER
  // One-time fix — syncs category field from items → inventory_summary
  // =================================================
  const syncInventoryCategories = async () => {
    if (!window.confirm(
      "This will sync category from Items Master → Inventory Summary for all items.\n\nProceed?"
    )) return;

    setMigrating(true);
    let updated = 0;
    let skipped = 0;

    try {
      // Fetch all items (active + inactive — we want full picture)
      const itemsSnap = await getDocs(query(collection(db, "items"), where("company_id", "==", companyId)));

      for (const itemDoc of itemsSnap.docs) {
        const item     = itemDoc.data();
        const itemId   = item.item_id || itemDoc.id;
        const category = item.category || "";

        if (!itemId || !category) { skipped++; continue; }

        // Try primary key (item_id = Firestore doc ID)
        const summaryRef = doc(db, "inventory_summary", itemId);
        try {
          await updateDoc(summaryRef, {
            category:   category,
            updated_at: serverTimestamp(),
          });
          updated++;
          console.log(`✅ Synced: ${item.item_name} → category: ${category}`);
        } catch (err) {
          // Primary key failed — try legacy doc ID formats (item_code, item_code variants)
          const legacyKeys = [
            item.item_code || "",                          // AST-6597
            (item.item_code || "").replace(/-/g, ""),      // AST6597
            item.item_name || "",                          // Angle Grinder
          ].filter(Boolean);

          let legacySynced = false;
          for (const legacyKey of legacyKeys) {
            try {
              const legacyRef = doc(db, "inventory_summary", legacyKey);
              await updateDoc(legacyRef, {
                category:   category,
                updated_at: serverTimestamp(),
              });
              updated++;
              console.log(`✅ Synced (legacy key: ${legacyKey}): ${item.item_name} → category: ${category}`);
              legacySynced = true;
              break;
            } catch {
              // try next key
            }
          }
          if (!legacySynced) {
            console.warn(`⚠ No inventory_summary found for ${item.item_name} — skipped`);
            skipped++;
          }
        }
      }

      alert(`✅ Category Sync Complete\nUpdated: ${updated} items\nSkipped: ${skipped} items`);
      fetchItems();

    } catch (err) {
      console.error("Sync Categories Error:", err);
      alert("Sync failed: " + err.message);
    } finally {
      setMigrating(false);
    }
  };

  // =================================================
  // CLEAN ORPHAN INVENTORY SUMMARY DOCS
  // Deletes inventory_summary docs for TOOLS/ASSET items
  // that are not part of material inventory
  // =================================================
  const cleanOrphanInventoryDocs = async () => {
    if (!window.confirm(
      "This will permanently DELETE inventory_summary docs for TOOLS and ASSET items.\n\nThese items belong to Asset Master, not inventory.\n\nProceed?"
    )) return;

    setMigrating(true);
    let deleted = 0;
    let skipped = 0;

    try {
      // deleteDoc imported at top of file

      const summarySnap = await getDocs(query(collection(db, "inventory_summary"), where("company_id", "==", companyId)));

      // Build items master lookup
      const itemsSnap = await getDocs(query(collection(db, "items"), where("company_id", "==", companyId)));
      const itemsMaster = {};
      itemsSnap.forEach(d => {
        const item = d.data();
        const byId   = (item.item_id   || "").trim();
        const byCode = (item.item_code || "").trim();
        if (byId)   itemsMaster[byId]   = item;
        if (byCode) itemsMaster[byCode] = item;
      });

      const EXCLUDED = ["TOOLS", "ASSET", "ASSETS"];

      for (const summaryDoc of summarySnap.docs) {
        const inv    = summaryDoc.data();
        const itemId = (inv.item_id   || summaryDoc.id || "").trim();
        const code   = (inv.item_code || "").trim();

        // Resolve category from items master first, then fallback to doc
        const masterItem = itemsMaster[itemId] || itemsMaster[code] || null;
        const resolvedCat = (
          masterItem?.category ||
          inv.category ||
          ""
        ).toUpperCase();

        // Also check item_code prefix for truly orphan docs
        const isOrphanByPrefix =
          code.startsWith("AS-") || code.startsWith("TL-") || code.startsWith("AST-");

        if (EXCLUDED.includes(resolvedCat) || isOrphanByPrefix) {
          await deleteDoc(summaryDoc.ref);
          deleted++;
          console.log(`🗑 Deleted orphan inventory_summary: ${inv.item_name} (${summaryDoc.id})`);
        } else {
          skipped++;
        }
      }

      alert(`✅ Cleanup Complete\nDeleted: ${deleted} orphan docs\nKept: ${skipped} valid docs`);

    } catch (err) {
      console.error("Cleanup Error:", err);
      alert("Cleanup failed: " + err.message);
    } finally {
      setMigrating(false);
    }
  };

  // =================================================
  // FIX GENERAL CATEGORIES — one-time migration
  // =================================================
  const fixGeneralCategories = async () => {
    if (!window.confirm(
      "Sab 'General' / blank category items ko 'MATERIAL' mein convert karega.\n\nProceed?"
    )) return;

    setMigrating(true);
    let fixed = 0;
    let skipped = 0;

    try {
      const itemsSnap = await getDocs(query(
        collection(db, "items"),
        where("company_id", "==", companyId)
      ));

      for (const itemDoc of itemsSnap.docs) {
        const item = itemDoc.data();
        const cat  = String(item.category || "").toUpperCase().trim();

        if (cat === "MATERIAL" || cat === "CONSUMABLE") { skipped++; continue; }

        await updateDoc(doc(db, "items", itemDoc.id), {
          category:   "MATERIAL",
          updated_at: serverTimestamp(),
        });

        const itemId = item.item_id || itemDoc.id;
        try {
          await updateDoc(doc(db, "inventory_summary", itemId), {
            category:   "MATERIAL",
            updated_at: serverTimestamp(),
          });
        } catch { /* inventory_summary nahi mili — ignore */ }

        fixed++;
        console.log(`✅ Fixed: ${item.item_name} (${cat || "blank"}) → MATERIAL`);
      }

      alert(`✅ Fix Complete\nFixed: ${fixed} items → MATERIAL\nSkipped: ${skipped} (already correct)`);
      fetchItems();

    } catch (err) {
      console.error("Fix General Error:", err);
      alert("Fix failed: " + err.message);
    } finally {
      setMigrating(false);
    }
  };

  // =================================================
  // EDIT ITEM
  // =================================================
  const handleEditSave = async () => {
    if (!editForm.item_name?.trim()) { alert("Item name required"); return; }
    if (!editForm.item_code?.trim()) { alert("Item code required"); return; }
    setEditSaving(true);
    try {
      await updateDoc(doc(db, "items", editingId), {
        item_name:             editForm.item_name.trim(),
        specification:         (editForm.specification || "").trim(),
        item_code:             editForm.item_code.trim(),
        unit:                  editForm.unit,
        category:              editForm.category,
        minimum_stock:         Number(editForm.minimum_stock || 0),
        preferred_vendor_id:   editForm.preferred_vendor_id   || "",
        preferred_vendor_name: editForm.preferred_vendor_name || "",
        updated_at:            serverTimestamp(),
      });
      // ✅ inventory_summary bhi sync karo
      const item = items.find(i => i.id === editingId);
      if (item?.item_id) {
        try {
          await updateDoc(doc(db, "inventory_summary", item.item_id), {
            item_name:             editForm.item_name.trim(),
            specification:         (editForm.specification || "").trim(),
            item_code:             editForm.item_code.trim(),
            unit:                  editForm.unit,
            category:              editForm.category,
            preferred_vendor_id:   editForm.preferred_vendor_id   || "",
            preferred_vendor_name: editForm.preferred_vendor_name || "",
            updated_at:            serverTimestamp(),
          });
        } catch { /* inventory_summary nahi mili — ignore */ }
      }
      setEditingId(null);
      setEditForm({});
      fetchItems();
      alert("✅ Item updated");
    } catch (err) {
      console.error("Edit Error:", err);
      alert("Failed to update: " + err.message);
    } finally {
      setEditSaving(false);
    }
  };

  // =================================================
  // DISABLE ITEM
  // =================================================

  const handleDisable =
    async (id) => {

      if (

        !window.confirm(
          "Disable this item?"
        )
      ) {

        return;
      }

      try {

        await updateDoc(

          doc(
            db,
            "items",
            id
          ),

          {

            is_active: false,

            updated_at:
              serverTimestamp(),
          }
        );

        fetchItems();

      } catch (err) {

        console.error(
          "Disable Error:",
          err
        );
      }
    };

  return (

    <div className="min-h-screen bg-gray-100 p-6">

      <div className="max-w-7xl mx-auto">

        {/* HEADER */}

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">

          <div className="flex items-center justify-between mb-6">

            <h2 className="text-2xl font-bold">
              Add New Item
            </h2>

            
          </div>

          {/* FORM */}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            <input
              type="text"
              name="item_name"
              placeholder="Item Name (short — e.g. 'Copper Pipe 22.2mm')"
              value={
                form.item_name
              }
              onChange={
                handleChange
              }
              className="border rounded-lg px-4 py-2"
            />

            <input
              type="text"
              name="specification"
              placeholder="Specification (optional — e.g. 'Softdrawn, 1.2mm thk, 19mm insulation')"
              value={form.specification}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2 md:col-span-2"
            />

            <input
              type="text"
              name="item_code"
              placeholder="Item Code"
              value={
                form.item_code
              }
              onChange={
                handleChange
              }
              className="border rounded-lg px-4 py-2"
            />

            <select
              name="unit"
              value={form.unit}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            >
              <option value="">— Select Unit *</option>
              <option value="nos">Nos</option>
              <option value="meter">Meter</option>
              <option value="kg">Kg</option>
              <option value="ltr">Ltr</option>
              <option value="box">Box</option>
              <option value="set">Set</option>
              <option value="rolls">Rolls</option>
              <option value="pcs">Pcs</option>
              <option value="sqft">Sqft</option>
              <option value="sqm">Sqm</option>
              <option value="rmt">Rmt</option>
              <option value="rft">Rft</option>
              <option value="job">Job</option>
              <option value="lot">Lot</option>
            </select>

            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            >
              <option value="MATERIAL">Material</option>
          
              <option value="CONSUMABLE">Consumable</option>
              
            </select>
 
            <input
              type="number"
              name="minimum_stock"
              placeholder="Min Stock (reorder level)"
              min="0"
              value={form.minimum_stock}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            />

            <select
              value={form.preferred_vendor_id}
              onChange={e => {
                const v = vendors.find(v => v.id === e.target.value);
                setForm(f => ({
                  ...f,
                  preferred_vendor_id:   v ? v.id : "",
                  preferred_vendor_name: v ? (v.vendor_name || v.name || "") : "",
                }));
              }}
              className="border rounded-lg px-4 py-2"
            >
              <option value="">— Preferred Vendor (Optional) —</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>
                  {v.vendor_name || v.name || v.id}
                </option>
              ))}
            </select>
          </div>

          {/* BUTTONS */}

          <div className="flex gap-4 mt-6">

            <button
              onClick={
                handleSubmit
              }
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold"
            >

              {

                loading

                  ? "Processing..."

                  : "Add Item"
              }
            </button>

            <button
              onClick={
                resetForm
              }
              className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded-lg"
            >
              Reset
            </button>
          </div>
        </div>

        {/* ITEMS */}

        <div className="bg-white rounded-xl shadow-md p-6">

          <h2 className="text-2xl font-bold mb-4">
            Items Master List
          </h2>

          <input
            type="text"
            placeholder="🔍 Search by name, specification, or code..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full mb-4"
          />

          {loading ? (

            <div>
              Loading...
            </div>

          ) : (

            <div className="overflow-x-auto">

              <table className="w-full border-collapse">

                <thead>

                  <tr className="bg-gray-800 text-white">

                    <th className="p-3 text-left">
                      Item ID
                    </th>

                    <th className="p-3 text-left">
                      Item Name
                    </th>

                    <th className="p-3 text-left">
                      Specification
                    </th>

                    <th className="p-3 text-left">
                      Code
                    </th>

                    <th className="p-3 text-left">
                      Unit
                    </th>

                    <th className="p-3 text-left">Category</th>
                    <th className="p-3 text-left">Min Stock</th>
                    <th className="p-3 text-left">Preferred Vendor</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items
                    .filter((item) => {
                      if (!searchTerm.trim()) return true;
                      const q = searchTerm.trim().toLowerCase();
                      return (
                        (item.item_name     || "").toLowerCase().includes(q) ||
                        (item.specification || "").toLowerCase().includes(q) ||
                        (item.item_code     || "").toLowerCase().includes(q)
                      );
                    })
                    .map((item) =>
                    editingId === item.id ? (
                      <tr key={item.id} className="border-b bg-blue-50">
                        <td className="p-3 font-semibold text-blue-600">
                          {item.item_id || "MISSING"}
                        </td>
                        <td className="p-3">
                          <input
                            value={editForm.item_name || ""}
                            onChange={e => setEditForm(f => ({ ...f, item_name: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-full"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            value={editForm.specification || ""}
                            onChange={e => setEditForm(f => ({ ...f, specification: e.target.value }))}
                            placeholder="Specification"
                            className="border rounded px-2 py-1 text-sm w-full"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            value={editForm.item_code || ""}
                            onChange={e => setEditForm(f => ({ ...f, item_code: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-full"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            value={editForm.unit || "nos"}
                            onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="nos">Nos</option>
                            <option value="meter">Meter</option>
                            <option value="kg">Kg</option>
                            <option value="box">Box</option>
                            <option value="set">Set</option>
                            <option value="ltr">Ltr</option>
                            <option value="rolls">Rolls</option>
                            <option value="pcs">Pcs</option>
                            <option value="sqft">Sqft</option>
                            <option value="sqm">Sqm</option>
                            <option value="rmt">Rmt</option>
                            <option value="rft">Rft</option>
                            <option value="job">Job</option>
                            <option value="lot">Lot</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <select
                            value={editForm.category || "MATERIAL"}
                            onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="MATERIAL">Material</option>
                            <option value="CONSUMABLE">Consumable</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <input
                            type="number" min="0"
                            value={editForm.minimum_stock || 0}
                            onChange={e => setEditForm(f => ({ ...f, minimum_stock: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-20"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            value={editForm.preferred_vendor_id || ""}
                            onChange={e => {
                              const v = vendors.find(v => v.id === e.target.value);
                              setEditForm(f => ({
                                ...f,
                                preferred_vendor_id:   v ? v.id : "",
                                preferred_vendor_name: v ? (v.vendor_name || v.name || "") : "",
                              }));
                            }}
                            className="border rounded px-2 py-1 text-sm w-40"
                          >
                            <option value="">— None —</option>
                            {vendors.map(v => (
                              <option key={v.id} value={v.id}>
                                {v.vendor_name || v.name || v.id}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">Active</td>
                        <td className="p-3 flex gap-2">
                          <button
                            onClick={handleEditSave}
                            disabled={editSaving}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                          >
                            {editSaving ? "..." : "Save"}
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setEditForm({}); }}
                            className="bg-gray-400 text-white px-3 py-1 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.id} className="border-b">
                        <td className="p-3 font-semibold text-blue-600">
                          {item.item_id || "MISSING"}
                        </td>
                        <td className="p-3">{item.item_name}</td>
                        <td className="p-3 text-sm text-slate-500">
                          {item.specification || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3">{item.item_code}</td>
                        <td className="p-3">{item.unit}</td>
                        <td className="p-3">{item.category}</td>
                        <td className="p-3 text-center">{item.minimum_stock || 0}</td>
                        <td className="p-3 text-sm text-slate-600">
                          {item.preferred_vendor_name || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3">{item.is_active ? "Active" : "Disabled"}</td>
                        <td className="p-3 flex gap-2">
                          <button
                            onClick={() => {
                              setEditingId(item.id);
                              setEditForm({
                                item_name:             item.item_name             || "",
                                specification:         item.specification         || "",
                                item_code:             item.item_code             || "",
                                unit:                  item.unit                  || "nos",
                                category:              item.category              || "MATERIAL",
                                minimum_stock:         item.minimum_stock         || 0,
                                preferred_vendor_id:   item.preferred_vendor_id   || "",
                                preferred_vendor_name: item.preferred_vendor_name || "",
                              });
                            }}
                            className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                          >
                            Edit
                          </button>
                          {item.is_active && (
                            <button
                              onClick={() => handleDisable(item.id)}
                              className="bg-red-500 text-white px-3 py-1 rounded"
                            >
                              Disable
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}